<template>
  <div>
    <div style="font-size: 1.25rem" class="px-4 font-weight-bold mt-4">
      {{ $vuetify.lang.t("$vuetify.ai_response.items_recommend") }}
    </div>
    <v-card-text>
      {{ $vuetify.lang.t("$vuetify.ai_response.input_hint") }}
    </v-card-text>
    <v-form ref="recommend_form" class="ml-4">
      <v-row>
        <v-col cols="6" md="6" xs="12">
          <v-text-field
            type="text"
            v-model="user_id"
            :label="$vuetify.lang.t('$vuetify.ai_response.user_id')"
            dense
            outlined
            :rules="[required($vuetify.lang.t('$vuetify.ai_response.user_id'))]"
          ></v-text-field>
        </v-col>
        <v-col cols="6" md="6" xs="12">
          <v-btn class="mr-4 primary--text" outlined @click="generateUserUUID">
            {{ $vuetify.lang.t("$vuetify.ai_response.autogenerate") }}
          </v-btn>
        </v-col>
        <v-col cols="6" md="6" xs="12">
          <v-text-field
            type="text"
            v-model="member_id"
            :label="$vuetify.lang.t('$vuetify.ai_response.member_id')"
            dense
            outlined
          ></v-text-field>
        </v-col>
        <v-col cols="6" md="6" xs="12">
          <v-btn class="mr-4 primary--text" outlined @click="generateMemberUUID">
            {{ $vuetify.lang.t("$vuetify.ai_response.autogenerate") }}
          </v-btn>
        </v-col>
        <v-col cols="6" md="6" xs="12">
          <v-text-field
            type="text"
            v-model="item_id"
            :label="$vuetify.lang.t('$vuetify.ai_response.item_id')"
            dense
            outlined
            :rules="[required($vuetify.lang.t('$vuetify.ai_response.item_id'))]"
          ></v-text-field>
        </v-col>
        <v-col cols="6" md="6" xs="12">
          <v-btn class="mr-4 primary--text" outlined @click="openModal = true">
            {{ $vuetify.lang.t("$vuetify.ai_response.choose_item") }}
          </v-btn>
        </v-col>
        <v-col cols="12">
          <span>
            {{ $vuetify.lang.t('$vuetify.ai_response.date_range_filter') }}
          </span>
        </v-col>
      </v-row>
      <template v-if="catalogItems.length > 0">
        <v-row  v-for="(keyItem, index) in keys" :key="index">
          <v-col cols="4">
            <v-select
              clearable
              :label='$vuetify.lang.t("$vuetify.ai_response.key")'
              :items="availableKeysForField(index, keyItem.key, catalogItems)"
              outlined
              dense
              :value="keyItem.key"
              @input="onSelectChange(index, 'key', $event)"
              :rules="keyRules(keyItem.key, keyItem.comparator, keyItem.date)"
            ></v-select>
          </v-col>

          <!-- Comparator Selector -->
          <v-col cols="4">
            <v-select
              clearable
              :label='$vuetify.lang.t("$vuetify.ai_response.comparison_operator")'
              :items="filteredComparators(keyItem.key, index, keys).map(c => ({value: c, text: getComparatorText(c)}))"
              outlined
              dense
              v-model="keyItem.comparator"
              @input="onSelectChange(index, 'comparator', $event)"
              :rules="comparatorRules(index, keys)"
            ></v-select>
          </v-col>

          <!-- Date Picker -->
          <v-col cols="4">
            <v-menu
              v-model="keyItem.menu"
              max-width="290px"
              min-width="290px"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              outlined
              dense
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  @input="onSelectChange(index, 'date', $event)"
                  clearable
                  :label='$vuetify.lang.t("$vuetify.catalog.date")'
                  prepend-icon="mdi-calendar"
                  readonly
                  v-bind="attrs"
                  :value="keyItem.date"
                  outlined
                  v-on="on"
                  dense
                  :rules="dateRules(keyItem.key, keyItem.comparator, keyItem.date)"
                ></v-text-field>
              </template>
              <v-date-picker clearable v-model="keyItem.date"></v-date-picker>
            </v-menu>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" style="margin-top: -10px;">
            <v-btn class="text-capitalize float-right ml-2" v-if="keys.length > 1" @click="removeKey" color="primary" dark>
              <span class="">
                <v-icon>mdi-minus-circle-outline</v-icon>
              </span>
            </v-btn>
            <v-btn class="text-capitalize float-right" @click="addNewKey" color="primary" dark>
              <span class="">
                <v-icon>mdi-plus-circle-outline</v-icon>
              </span>
            </v-btn>
          </v-col>
        </v-row>
      </template>
      <v-row>
        <v-col cols="12">
          <v-btn class="ml-2" color="primary" @click="getRecommendedItems" dark>
            {{ $vuetify.lang.t("$vuetify.ai_response.get_response") }}
          </v-btn>
        </v-col>
      </v-row>
    </v-form>
    <v-dialog v-model="openModal" max-width="90%">
      <template v-if="isLoading">
        <Loader class="mr-2 mt-6" />
      </template>
      <v-card v-else class="d-flex flex-column" style="
          overflow-y: scroll;
          overflow-x: hidden;
          min-height: 80vh;
          position: relative;
        ">
        <v-container>
          <div style="font-size: 1.25rem" class="font-weight-medium mt-8">
            {{ $vuetify.lang.t("$vuetify.ai_response.choose_item") }}
          </div>
        </v-container>
        <v-container>
          <v-row>
            <v-col cols="12" md="3">
              <v-text-field
                v-model="searchText"
                :label="$vuetify.lang.t('$vuetify.insights.search')"
                outlined
                dense
                single-line
                hide-details
                append-icon="mdi-magnify"
                @input="debouncedFetchCatalogItems"
              ></v-text-field>
            </v-col>
          </v-row>
          <v-row>
            <v-col v-for="product in catalogItems" :key="product.item[mapper.item_id]" cols="12" md="3">
              <v-card class="product-card" outlined @click="setItemId(product.item)">
                <v-img :src="product.item[mapper.image_url]" height="100px">
                  <template #placeholder>
                    <div class="d-flex align-center justify-center grey--text text-caption" style="height: 100%">
                      {{ $vuetify.lang.t("$vuetify.ai_response.image_not_available") }}
                    </div>
                  </template>
                </v-img>
                <div class="card-content">
                  <p class="ml-4">
                    {{ product.item[mapper.title].slice(0, 50) }}
                  </p>
                  <strong class="ml-4">¥ {{ product.item[mapper.price] }}</strong>
                </div>
              </v-card>
            </v-col>
          </v-row>
        </v-container>

        <!-- pagination -->
        <v-container>
          <v-row>
            <v-col class="py-0" cols="12" md="12">
              <v-divider></v-divider>
            </v-col>
            <v-col cols="12" xs="12" sm="6" md="4" class="d-flex">
            <span v-if="!isLoading" class="ma-2 font-weight-bold">
              {{ $vuetify.lang.t("$vuetify.common.page_no") }} : <span class="primary--text"> {{ catalogScrollIdStack.length }} / {{ pageCount }} </span>
            </span>
            </v-col>
            <v-col cols="12" xs="12" sm="6" md="6" class="d-flex">
              <v-btn class="ma-2" color="primary" :disabled="boundary === 'left' || boundary === 'both' || isLoading" @click="handlePreviousPage">
                <v-icon left>mdi-chevron-left</v-icon>
                Previous
              </v-btn>

              <v-btn class="ma-2" color="primary" :disabled="boundary === 'right' || boundary === 'both' || isLoading" @click="handleNextPage">
                Next
                <v-icon right>mdi-chevron-right</v-icon>
              </v-btn>
            </v-col>
            <v-col cols="12" xs="6" sm="3" md="2" class="d-flex justify-end">
              <v-select
                v-model="itemsPerPage"
                :items="rows"
                hide-details="auto"
                outlined
                dense
              ></v-select>
            </v-col>
          </v-row>
        </v-container>

        <v-divider></v-divider>
        <v-card-title>
          <v-card-actions class="justify-end" style="position: absolute; bottom: 0; right: 0">
            <v-btn color="primary" text @click="openModal = false">
              {{
                $vuetify.lang.t("$vuetify.ai_response.close")
              }}
            </v-btn>
          </v-card-actions>
        </v-card-title>
      </v-card>
    </v-dialog>
    <v-dialog v-model="openResponseModal" max-width="90%">
      <template v-if="responseLoading">
        <Loader class="mr-2 mt-6" />
      </template>
      <v-card v-else class="d-flex flex-column" style="
          overflow-y: scroll;
          overflow-x: hidden;
          min-height: 80vh;
          position: relative;
        ">
        <v-container>
          <div style="font-size: 1.25rem" class="font-weight-medium mt-8">
            {{ $vuetify.lang.t("$vuetify.ai_response.selected_item") }}
          </div>
        </v-container>
        <v-container>
          <v-row>
            <v-col cols="12" md="3">
              <v-card class="product-card" @click="copyItemToClipBoard(selectedItem)" outlined>
                <v-img :src="selectedItem[mapper.image_url]" height="100px">
                  <template #placeholder>
                    <div class="d-flex align-center justify-center grey--text text-caption" style="height: 100%">
                      {{ $vuetify.lang.t("$vuetify.ai_response.image_not_available") }}
                    </div>
                  </template>
                </v-img>
                <div class="card-content">
                  <p class="ml-4">
                    {{
                      selectedItem[mapper.title] &&
                      selectedItem[mapper.title].slice(0, 50)
                    }}
                  </p>
                  <strong class="ml-4">¥ {{ selectedItem[mapper.price] }}</strong>
                </div>
              </v-card>
            </v-col>
          </v-row>
        </v-container>

        <v-container>
          <div style="font-size: 1.25rem" class="font-weight-medium mt-8">
            {{ $vuetify.lang.t("$vuetify.ai_response.items_recommend") }}
          </div>
        </v-container>
        <v-container >
          <v-row v-if="recommendedItems && recommendedItems.length > 0">
            <v-col v-for="product in recommendedItems" :key="product[mapper.item_id]" cols="12" md="3">
              <v-card class="product-card" @click="copyItemToClipBoard(product)" outlined>
                <v-img :src="product[mapper.image_url]" height="100px">
                  <template #placeholder>
                    <div class="d-flex align-center justify-center grey--text text-caption" style="height: 100%">
                      {{ $vuetify.lang.t("$vuetify.ai_response.image_not_available") }}
                    </div>
                  </template>
                </v-img>
                <div class="card-content">
                  <p class="ml-4">{{ product[mapper.title].slice(0, 50) }}</p>
                  <strong class="ml-4">¥ {{ product[mapper.price] }}</strong>
                </div>
              </v-card>
            </v-col>
          </v-row>
          <v-col v-else  class="d-flex align-items-center pa-0">
            <v-icon class="text-info mr-2">mdi-information</v-icon>
            <span class="text-body-1">
              {{ $vuetify.lang.t("$vuetify.ai_response.recommended_items_not_found") }}
            </span>
          </v-col>
        </v-container>
        <v-divider></v-divider>
        <v-card-title>
          <v-card-actions class="justify-end" style="position: absolute; bottom: 0; right: 0">
            <v-btn color="primary" text @click="openResponseModal = false">{{
                $vuetify.lang.t("$vuetify.ai_response.close")
              }}</v-btn>
          </v-card-actions>
        </v-card-title>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import _ from 'lodash';
import { v4 as uuidv4 } from "uuid";
import { FETCH_ITEMS_RECOMMEND, ADD_ALERT, GET_CATALOG_ITEMS } from "@/store/_actiontypes";
import { mapState, mapActions } from "vuex";
import Loader from "@/components/common/loader/Loader";
import { filteredComparators, comparatorRules, keyRules, dateRules, availableKeysForField, getComparatorText} from '../../Recommendation/shared_libs';
export default {
  components: {
    Loader,
  },
  props: {
    mapper: {
      type: Object,
    },
  },
  data() {
    return {
      user_id: "",
      member_id: "",
      item_id: "",
      upper_bound: "",
      lower_bound: "",
      dateErrorMessage: "",
      menu: false,
      dates: [],
      keys: [
        {key: "", date: "", comparator: "",  menu: false},
      ],
      previousKeys: {},
      startMenu: false,
      endMenu: false,
      startDate: null,
      endDate: null,
      openModal: false,
      openResponseModal: false,
      recommendedItems: [],
      responseLoading: false,
      selectedItem: {},
      selectedKeys: {},
      itemsPerPage: 10,
      rows: [5, 10, 20],
      catalogScrollIdStack: [],
      searchText: "",
      boundary: "",
      isLoading: false,
      pageCount: 1,
      catalogItems: [],
    };
  },
  computed: {
    ...mapState({
      selectedProject: (state) => state.project.selectedProject,
    }),
    hasDateError() {
      return !!this.dateErrorMessage;
    },

  },

  mounted(){
    this.getCatalogItems()
  },
  created(){
    this.debouncedFetchCatalogItems = _.debounce(() => {
      this.catalogScrollIdStack.pop()
      this.catalogScrollIdStack.pop()
      this.getCatalogItems()
    }, 500);
  },
  watch: {
    itemsPerPage: {
      handler() {
        this.catalogScrollIdStack = []
        this.getCatalogItems();
      },
      immediate: false,
    },
  },
  methods: {
    ...mapActions("ai_response", [FETCH_ITEMS_RECOMMEND]),
    ...mapActions("project", [GET_CATALOG_ITEMS]),
    ...mapActions("alert", [ADD_ALERT]),
    filteredComparators,
    comparatorRules,
    keyRules,
    dateRules,
    availableKeysForField,
    getComparatorText,
    required(fieldName) {
      return (value) => !!value || `${fieldName} ${this.$vuetify.lang.t("$vuetify.ai_response.is_required")}`;
    },
    addNewKey(){
      this.keys.push({key: "", date: "", comparator: "",  menu: false})
    },
    removeKey(){
      const keysLength = this.keys.length
      if(keysLength > 1){
        const key = this.keys[keysLength-1].key
        if(this.selectedKeys[key])
          this.selectedKeys[key] = this.selectedKeys[key] - 1
        this.keys.pop()
      }
    },
    handleNextPage() {
      this.getCatalogItems();
    },
    handlePreviousPage() {
      this.catalogScrollIdStack.pop()
      this.catalogScrollIdStack.pop()
      this.getCatalogItems();
    },
    generateMemberUUID() {
      this.member_id = uuidv4();
    },
    generateUserUUID() {
      this.user_id = uuidv4();
    },
    copyItemToClipBoard(item){
      navigator.clipboard.writeText(JSON.stringify(item, null, 2));
      this.$store.dispatch(
        `alert/${ADD_ALERT}`, {
          message: this.$vuetify.lang.t("$vuetify.ai_response.item_copied_message"),
          color: "success",
        }, { root: true }
      );
    },
    getCatalogItems(){
      const payload = {
        text: this.searchText || null,
        size: this.itemsPerPage
      }
      const catalogScrollIdStackLength = this.catalogScrollIdStack.length
      if (catalogScrollIdStackLength !== 0) {
        payload["scroll_id"] = this.catalogScrollIdStack [catalogScrollIdStackLength - 1]
      }
      this.isLoading = true
      let count = 1
      this.GET_CATALOG_ITEMS({
        project_id: this.selectedProject.id,
        payload
      }).then(
        (response) => {
          if (response?.data?.detail?.response?.total_hits) {
            count = Math.ceil(
              response?.data?.detail?.response?.total_hits / this.itemsPerPage
            );
            this.pageCount = count
          }


          this.catalogItems = response.data.detail.response?.items
          if (response?.data?.detail?.response?.next_scroll_id){
            this.catalogScrollIdStack = [...this.catalogScrollIdStack, response?.data?.detail?.response?.next_scroll_id]
          }
          this.boundary =
            count === 1 ? "both" :
              this.catalogScrollIdStack.length === 1 ? "left" :
                count === this.catalogScrollIdStack.length ? "right" : "";
        },
        (error) => {
          console.error("error", error)
        }
      ).finally(()=> {
        this.isLoading = false
      })
    },
    setItemId(item) {
      this.selectedItem = item;
      navigator.clipboard.writeText(JSON.stringify(item, null, 2));
      this.$store.dispatch(
        `alert/${ADD_ALERT}`, {
          message: this.$vuetify.lang.t("$vuetify.ai_response.item_selected_message"),
          color: "success",
        }, { root: true }
      );
      this.item_id = item[this.mapper.item_id];
      this.openModal = false;
    },
    onSelectChange(index, field, value) {
      if (value === null) {
        this.keys[index][field] = "";
      } else {
        this.keys[index][field] = value;
      }
      let keyValues = {}
      this.keys.forEach((key) => {
        if( key.key && keyValues[key.key] == null){
          keyValues[key.key] = 1
        }
        else if( key.key && keyValues[key.key] != null){
          keyValues[key.key] = keyValues[key.key] + 1
          const indexes = []
          this.keys.forEach((item, index)=> {
            if(item.key == key.key){
              indexes.push(index)
              return true
            }
            return false
          })
          const subStr = (this.keys[indexes[0]].comparator.includes("gt") && this.keys[indexes[1]].comparator.includes("gt")) || (this.keys[indexes[0]].comparator.includes("lt") && this.keys[indexes[1]].comparator.includes("lt"))
          if( subStr ){
            this.keys[indexes[1]].comparator = ""
          }
        }
      })
      this.selectedKeys = keyValues
      setTimeout(()=> {
        this.validateForm()
      }, 100)
    },
    getFormData() {
      return {
        member_id: this.member_id,
        user_id: this.user_id,
        item_id: this.item_id,
      };
    },
    validateForm() {
      return this.$refs.recommend_form.validate();
    },
    getRecommendedItems() {
      const valid = this.validateForm();
      if (!valid) return;
      const keys = {}
      this.keys.forEach(keyItem => {
        if(keys[keyItem.key] && keyItem.key)
          keys[keyItem.key] = {...keys[keyItem.key], key: keyItem.key, [keyItem.comparator] : keyItem.date}
        else if (keyItem.key)
          keys[keyItem.key] = {key: keyItem.key, [keyItem.comparator] : keyItem.date}
      })
      const date_range_filters = Object.values(keys)
      this.openResponseModal = true;
      this.responseLoading = true;

      this.FETCH_ITEMS_RECOMMEND({
        project_id: this.selectedProject.id,
        user_id: this.user_id,
        member_id: this.member_id,
        item_id: this.item_id,
        date_range_filters
      }).then(
        (response) => {
          this.responseLoading = false;
          this.recommendedItems = response.data.detail.response;
        },
        (error) => {
          this.openResponseModal = false;
          if(error.status === 422){
            this.$store.dispatch(
              `alert/${ADD_ALERT}`, {
                message: JSON.stringify(error?.response?.data?.detail),
                color: "error",
              }, { root: true }
            );
          } else {
            this.$store.dispatch(
              `alert/${ADD_ALERT}`, {
                message: error?.response?.data?.detail?.response,
                color: "error",
              }, { root: true }
            );
          }
          console.error("error", error);
        }
      );
    },
  },
};
</script>

<style scoped>
.product-card {
  height: 300px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  cursor: pointer;
}
.card-content {
  padding: 4px;
}

.card-content .v-card-title {
  margin-bottom: 0;
  font-size: 16px;
}

.card-content .v-card-subtitle {
  margin-bottom: 8px;
}
</style>
